// Firebase utilities for authentication

import * as Sentry from '@sentry/react'
import { initializeApp } from 'firebase/app'
import {
    getAuth,
    initializeAuth,
    indexedDBLocalPersistence,
    type Auth,
    signInWithEmailAndPassword,
    signOut as firebaseSignOut,
    onAuthStateChanged,
    User,
    GoogleAuthProvider,
    OAuthProvider,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    signInWithCredential,
    UserCredential,
    sendPasswordResetEmail as firebaseSendPasswordResetEmail,
    confirmPasswordReset as firebaseConfirmPasswordReset,
    ActionCodeSettings,
} from 'firebase/auth'
import { FirebaseAuthentication } from '@capacitor-firebase/authentication'
// import { Device } from '@capacitor/device' // Commented out - used by isIOSSimulator which is currently disabled for testing
// Firebase Analytics is disabled for Capacitor builds
// Firebase Analytics doesn't work in Capacitor WebView and causes errors
// All analytics in this app uses PostHog instead

// Check if we're in Capacitor to conditionally exclude measurementId
// Firebase may try to auto-initialize analytics if measurementId is present
// Check multiple ways to detect Capacitor since it might not be loaded yet
function isCapacitorEnvironment(): boolean {
    if (typeof window === 'undefined') return false

    try {
        // Check for Capacitor global
        // @ts-ignore
        if (window.Capacitor?.isNativePlatform?.()) {
            return true
        }
        // Check user agent for Capacitor
        const ua = navigator.userAgent || ''
        if (ua.includes('Capacitor') || ua.includes('capacitor')) {
            return true
        }
        // Check if running in Android/iOS WebView (common Capacitor indicators)
        if (ua.includes('Android') && ua.includes('wv')) {
            return true
        }
        // Check for Capacitor-specific window properties
        const win = window as any
        if (win.CapacitorWeb || win.Android || win.webkit?.messageHandlers) {
            return true
        }
    } catch {
        // Ignore errors
    }

    return false
}

// Always exclude measurementId in Capacitor to prevent analytics auto-initialization
const isCapacitor = isCapacitorEnvironment()

/**
 * Detect if the app is running in an in-app browser (Instagram, Facebook, TikTok, etc.)
 * These browsers block popups, so we need to use redirect-based authentication
 */
function isInAppBrowser(): boolean {
    if (typeof window === 'undefined') return false

    const ua = navigator.userAgent || ''
    const uaLower = ua.toLowerCase()

    // Common in-app browser patterns
    const inAppPatterns = [
        'instagram', // Instagram
        'fban', // Facebook Android
        'fbav', // Facebook iOS
        'bytedancewebview', // TikTok
        'musical_ly', // TikTok (legacy)
        'twitter', // Twitter/X
        'linkedinapp', // LinkedIn
        'snapchat', // Snapchat
        'pinterest', // Pinterest
        'micromessenger', // WeChat
        'line', // Line
    ]

    return inAppPatterns.some((pattern) => uaLower.includes(pattern))
}

/**
 * Detect if we're running in iOS Chrome or another iOS browser that's not Safari.
 * These browsers use WKWebView which has issues with popup-based authentication.
 * signInWithPopup fails on iOS Chrome/Firefox/Edge due to WKWebView limitations.
 */
function isIOSNonSafariBrowser(): boolean {
    if (typeof window === 'undefined') return false

    const ua = navigator.userAgent || ''

    // Check if iOS (iPad, iPhone, iPod)
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream

    if (!isIOS) return false

    // Detect non-Safari iOS browsers by their unique UA identifiers:
    // - Chrome on iOS: "CriOS"
    // - Firefox on iOS: "FxiOS"
    // - Edge on iOS: "EdgiOS"
    // - Opera on iOS: "OPT/"
    const isChrome = /CriOS/.test(ua)
    const isFirefox = /FxiOS/.test(ua)
    const isEdge = /EdgiOS/.test(ua)
    const isOpera = /OPT\//.test(ua)

    return isChrome || isFirefox || isEdge || isOpera
}

/**
 * Check if the app is running on an iOS simulator.
 * Apple Sign-in does not work on simulators - it requires a real device.
 * Currently commented out for testing - uncomment when ready to restore simulator check
 */
// async function isIOSSimulator(): Promise<boolean> {
//     if (!isCapacitor) return false
//
//     try {
//         const info = await Device.getInfo()
//         // isVirtual is true for simulators/emulators
//         return info.platform === 'ios' && info.isVirtual
//     } catch (error) {
//         console.error('Error detecting simulator:', error)
//         return false
//     }
// }

const firebaseConfig: any = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    // Don't include measurementId in Capacitor to prevent analytics auto-initialization
    ...(isCapacitor
        ? {}
        : { measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID }),
}

// Initialize Firebase
const app = initializeApp(firebaseConfig)

// Initialize auth with proper persistence for native platforms
let auth: Auth
if (isCapacitor) {
    // iOS / Android Capacitor build
    auth = initializeAuth(app, {
        persistence: indexedDBLocalPersistence,
    })
} else {
    // Normal web / PWA build
    auth = getAuth(app)
}

const googleProvider = new GoogleAuthProvider()

// Firebase Analytics is not initialized - it's not used in this app
// All analytics tracking uses PostHog instead
// Export undefined for backward compatibility
const analytics = undefined

/**
 * Promise that resolves when Firebase Auth is initialized (user known, even if null)
 */
let authInitPromise: Promise<User | null> | null = null

/**
 * Wait for Firebase Auth to finish initializing (user known, even if null)
 */
export function waitForAuthInit(): Promise<User | null> {
    if (!authInitPromise) {
        authInitPromise = new Promise((resolve) => {
            const unsub = onAuthStateChanged(auth, (user) => {
                unsub()
                resolve(user)
            })
        })
    }
    return authInitPromise
}

/**
 * Get the current user's ID token
 * @param forceRefresh - If true, forces a token refresh even if cached token is still valid
 */
export async function getIdToken(forceRefresh = false): Promise<string | null> {
    const currentUser = auth.currentUser

    if (!currentUser) {
        return null
    }

    try {
        return await currentUser.getIdToken(forceRefresh)
    } catch (error) {
        console.error('Error getting ID token:', error)
        return null
    }
}

/**
 * Sign in with email and password
 */
export async function signIn(email: string, password: string): Promise<User> {
    const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
    )
    return userCredential.user
}

/**
 * Sign in with Google
 * Uses native Google Sign-In on Capacitor (iOS/Android)
 * Uses redirect for:
 *   - In-app browsers (Instagram, Facebook, etc.) that block popups
 *   - iOS non-Safari browsers (Chrome, Firefox, Edge) that use WKWebView
 *     which has issues with popup-based authentication
 * Uses popup for normal browsers (better UX)
 */
export async function signInWithGoogle(
    returnToPath?: string
): Promise<UserCredential | null> {
    console.log('[signInWithGoogle] Starting, isCapacitor:', isCapacitor)
    // Add scopes for additional profile information if needed
    googleProvider.addScope('profile')
    googleProvider.addScope('email')

    // Use native Google Sign-In on Capacitor (iOS/Android) with skipNativeAuth
    // This creates credentials on the native layer, then signs in with Firebase JS SDK
    if (isCapacitor) {
        try {
            console.log('[signInWithGoogle] Calling native Google Sign-In...')
            // Pass skipNativeAuth: true to prevent double Firebase initialization
            const result = await FirebaseAuthentication.signInWithGoogle({
                skipNativeAuth: true,
            })
            console.log(
                '[signInWithGoogle] Native call returned:',
                result ? 'success' : 'null'
            )

            // Validate that we received a valid credential
            const idToken = result.credential?.idToken
            if (!idToken) {
                console.log(
                    '[signInWithGoogle] No idToken - user likely cancelled'
                )
                // User likely cancelled the sign-in flow
                return null
            }

            console.log(
                '[signInWithGoogle] Got idToken, signing in with Firebase...'
            )
            // Sign in with Firebase JS SDK using the ID token from native auth
            const credential = GoogleAuthProvider.credential(idToken)
            return signInWithCredential(auth, credential)
        } catch (error: any) {
            console.log(
                '[signInWithGoogle] Error:',
                error?.code,
                error?.message
            )
            // Handle user cancellation - Capacitor plugin may throw instead of returning null
            if (
                error.message?.toLowerCase().includes('cancel') ||
                error.code === 'CANCELED' ||
                error.code === 12501 || // Google Sign-In SIGN_IN_CANCELLED
                error.code === '12501'
            ) {
                console.log('[signInWithGoogle] User cancelled')
                return null
            }
            // Re-throw other errors to be handled by caller
            console.log('[signInWithGoogle] Re-throwing error')
            throw error
        }
    }

    // Use redirect auth for browsers that have issues with popups
    const shouldUseRedirect = isInAppBrowser() || isIOSNonSafariBrowser()

    if (shouldUseRedirect) {
        // In-app browsers block popups, and iOS non-Safari browsers (Chrome, Firefox, etc.)
        // have issues with popup auth due to WKWebView limitations - use redirect instead
        // Store returnToPath in sessionStorage only for redirect flow
        if (returnToPath) {
            sessionStorage.setItem('googleAuthReturnToPath', returnToPath)
        }
        await signInWithRedirect(auth, googleProvider)
        // Redirect will navigate away, so return null
        // The result will be handled by getGoogleRedirectResult() when user returns
        return null
    } else {
        // Normal browsers can use popup (better UX)
        // No need to store returnToPath in sessionStorage for popup flow
        return signInWithPopup(auth, googleProvider)
    }
}

/**
 * Check for and handle Google redirect result after user returns from Google sign-in
 * Call this on mount of login/register pages to handle redirect-based authentication
 * @deprecated Use getOAuthRedirectResult() instead to handle both Google and Apple redirects
 */
export async function getGoogleRedirectResult(): Promise<{
    credential: UserCredential | null
    returnToPath: string | null
}> {
    try {
        const result = await getRedirectResult(auth)
        const returnToPath = sessionStorage.getItem('googleAuthReturnToPath')
        sessionStorage.removeItem('googleAuthReturnToPath')

        return {
            credential: result,
            returnToPath: returnToPath || null,
        }
    } catch (error) {
        console.error('Error getting Google redirect result:', error)
        sessionStorage.removeItem('googleAuthReturnToPath')
        return {
            credential: null,
            returnToPath: null,
        }
    }
}

/**
 * Wrapper around signInWithPopup that reliably detects popup closure.
 *
 * Firebase's signInWithPopup doesn't always detect when Apple's OAuth popup is closed.
 * This wrapper intercepts window.open to capture the popup reference, then polls
 * popup.closed to know exactly when the user closes it.
 */
function signInWithPopupAndMonitor(
    authInstance: Auth,
    provider: OAuthProvider
): Promise<UserCredential> {
    return new Promise((resolve, reject) => {
        let popupWindow: Window | null = null
        let pollInterval: ReturnType<typeof setInterval> | null = null
        let resolved = false

        // Cleanup function to clear interval
        const cleanup = () => {
            if (pollInterval) {
                clearInterval(pollInterval)
                pollInterval = null
            }
        }

        // Temporarily intercept window.open to capture the popup reference
        const originalOpen = window.open.bind(window)
        window.open = function (...args: Parameters<typeof window.open>) {
            popupWindow = originalOpen(...args)
            return popupWindow
        }

        // Start the sign-in process (this internally calls window.open)
        const signInPromise = signInWithPopup(authInstance, provider)

        // Restore original window.open immediately
        window.open = originalOpen

        // If we captured the popup window, poll for closure
        if (popupWindow) {
            pollInterval = setInterval(() => {
                if (popupWindow?.closed && !resolved) {
                    cleanup()
                    // Small grace period to allow auth to complete if it's in flight
                    setTimeout(() => {
                        if (!resolved) {
                            resolved = true
                            reject({ code: 'auth/popup-closed-by-user' })
                        }
                    }, 500)
                }
            }, 200) // Check every 200ms
        }

        // Handle the sign-in promise
        signInPromise
            .then((result) => {
                if (!resolved) {
                    resolved = true
                    cleanup()
                    resolve(result)
                }
            })
            .catch((error) => {
                if (!resolved) {
                    resolved = true
                    cleanup()
                    reject(error)
                }
            })
    })
}

/**
 * Sign in with Apple
 * Uses native Apple Sign-In on Capacitor (iOS)
 * Uses redirect for:
 *   - In-app browsers (Instagram, Facebook, etc.) that block popups
 *   - iOS non-Safari browsers (Chrome, Firefox, Edge) that use WKWebView
 *     which has issues with popup-based authentication
 * Uses popup for normal browsers (better UX)
 */
export async function signInWithApple(
    returnToPath?: string
): Promise<UserCredential | null> {
    const appleProvider = new OAuthProvider('apple.com')
    appleProvider.addScope('email')
    appleProvider.addScope('name')

    // Use native Apple Sign-In on Capacitor (iOS) - let native Firebase SDK handle auth
    // Note: We do NOT pass skipNativeAuth here (defaults to false) because:
    // - skipNativeAuth: true returns an Apple ID token with audience = Bundle ID (com.style.wtf)
    // - Firebase JS SDK expects audience = Service ID (com.style.wtf.web)
    // - This mismatch causes "audience does not match" errors
    // - By letting native Firebase SDK handle auth, it correctly validates using Bundle ID
    if (isCapacitor) {
        // TODO: Uncomment after testing - simulator check temporarily disabled
        // Check if running on simulator - Apple Sign-in doesn't work on simulators
        // const isSimulator = await isIOSSimulator()
        // if (isSimulator) {
        //     throw new Error(
        //         'Apple Sign-in is not supported on iOS Simulator. Please test on a real device with an Apple ID signed in.'
        //     )
        // }

        try {
            // Use skipNativeAuth: true to get raw Apple credential
            // Then sign in with Firebase JS SDK using the credential
            console.log(
                '[Apple Sign-In] Starting native sign-in with skipNativeAuth: true...'
            )
            Sentry.addBreadcrumb({
                category: 'auth',
                message: 'Apple Sign-In started (skipNativeAuth)',
                level: 'info',
            })

            const result = await FirebaseAuthentication.signInWithApple({
                skipNativeAuth: true,
            })
            console.log(
                '[Apple Sign-In] Native result:',
                JSON.stringify({
                    hasUser: !!result.user,
                    hasCredential: !!result.credential,
                    hasIdToken: !!result.credential?.idToken,
                })
            )

            // Validate that we received a valid credential
            const idToken = result.credential?.idToken
            if (!idToken) {
                // User cancelled the sign-in flow
                console.log(
                    '[Apple Sign-In] No idToken in credential - user cancelled'
                )
                return null
            }

            console.log(
                '[Apple Sign-In] Got Apple credential, signing in with Firebase JS SDK...'
            )

            // Sign in with Firebase JS SDK using the Apple credential
            const credential = appleProvider.credential({
                idToken: idToken,
                rawNonce: result.credential?.nonce,
            })

            console.log('[Apple Sign-In] Calling signInWithCredential...')
            const userCredential = await signInWithCredential(auth, credential)
            console.log(
                '[Apple Sign-In] signInWithCredential succeeded, user:',
                userCredential.user?.uid
            )

            return userCredential
        } catch (error: any) {
            console.error('[Apple Sign-In] Error caught:', {
                message: error.message,
                code: error.code,
                name: error.name,
            })
            Sentry.addBreadcrumb({
                category: 'auth',
                message: `Apple Sign-In error: ${error.message} (code: ${error.code})`,
                level: 'error',
            })

            // Handle user cancellation - Capacitor plugin may throw instead of returning null
            if (
                error.message?.toLowerCase().includes('cancel') ||
                error.code === 'CANCELED' ||
                error.code === 1000 || // Apple ASAuthorizationError.unknown (often thrown on cancel)
                error.code === 1001 // Apple ASAuthorizationError.canceled
            ) {
                console.log(
                    '[Apple Sign-In] Detected as cancellation, returning null'
                )
                return null
            }
            // Re-throw other errors to be handled by caller
            console.log('[Apple Sign-In] Re-throwing error to caller')
            throw error
        }
    }

    // Use redirect auth for browsers that have issues with popups
    const shouldUseRedirect = isInAppBrowser() || isIOSNonSafariBrowser()

    if (shouldUseRedirect) {
        // Store returnToPath in sessionStorage only for redirect flow
        if (returnToPath) {
            sessionStorage.setItem('appleAuthReturnToPath', returnToPath)
        }
        await signInWithRedirect(auth, appleProvider)
        // Redirect will navigate away, so return null
        return null
    } else {
        // Normal browsers can use popup (better UX)
        // Use wrapper that monitors for popup closure since Firebase doesn't
        // reliably detect when Apple's OAuth popup is closed
        return signInWithPopupAndMonitor(auth, appleProvider)
    }
}

/**
 * Check for and handle Apple redirect result after user returns from Apple sign-in
 * Call this on mount of login/register pages to handle redirect-based authentication
 * @deprecated Use getOAuthRedirectResult() instead to handle both Google and Apple redirects
 */
export async function getAppleRedirectResult(): Promise<{
    credential: UserCredential | null
    returnToPath: string | null
}> {
    try {
        const result = await getRedirectResult(auth)
        const returnToPath = sessionStorage.getItem('appleAuthReturnToPath')
        sessionStorage.removeItem('appleAuthReturnToPath')

        return {
            credential: result,
            returnToPath: returnToPath || null,
        }
    } catch (error) {
        console.error('Error getting Apple redirect result:', error)
        sessionStorage.removeItem('appleAuthReturnToPath')
        return {
            credential: null,
            returnToPath: null,
        }
    }
}

/**
 * Unified OAuth redirect result handler
 * Handles both Google and Apple redirect-based sign-in results.
 * Firebase's getRedirectResult() can only be consumed once, so this function
 * should be used instead of the separate getGoogleRedirectResult/getAppleRedirectResult
 * to avoid race conditions where one handler consumes the other's credentials.
 */
export async function getOAuthRedirectResult(): Promise<{
    credential: UserCredential | null
    providerId: 'google.com' | 'apple.com' | null
    returnToPath: string | null
}> {
    try {
        const result = await getRedirectResult(auth)

        if (!result) {
            return { credential: null, providerId: null, returnToPath: null }
        }

        // Determine provider from the credential
        const providerId = result.providerId as
            | 'google.com'
            | 'apple.com'
            | null

        // Get the appropriate returnToPath based on provider
        let returnToPath: string | null = null
        if (providerId === 'google.com') {
            returnToPath = sessionStorage.getItem('googleAuthReturnToPath')
            sessionStorage.removeItem('googleAuthReturnToPath')
        } else if (providerId === 'apple.com') {
            returnToPath = sessionStorage.getItem('appleAuthReturnToPath')
            sessionStorage.removeItem('appleAuthReturnToPath')
        }

        return { credential: result, providerId, returnToPath }
    } catch (error) {
        console.error('Error getting OAuth redirect result:', error)
        // Clean up both storage keys on error
        sessionStorage.removeItem('googleAuthReturnToPath')
        sessionStorage.removeItem('appleAuthReturnToPath')
        throw error
    }
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<void> {
    return firebaseSignOut(auth)
}

/**
 * Subscribe to auth state changes
 */
export function subscribeToAuthChanges(
    callback: (user: User | null) => void
): () => void {
    return onAuthStateChanged(auth, callback)
}

/**
 * Get the current authenticated user
 */
export function getCurrentUser(): User | null {
    return auth.currentUser
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
    return !!auth.currentUser
}

/**
 * Send password reset email
 */
export async function sendPasswordReset(email: string): Promise<void> {
    // Build action code settings so the link returns to our PWA
    // Use Vite's BASE_URL to ensure the action URL matches the PWA's base path
    const basePath = import.meta.env.BASE_URL.replace(/\/$/, '') // Remove trailing slash if present
    const actionCodeSettings: ActionCodeSettings = {
        // After clicking the reset link, the user will be redirected to our action handler
        url: `${window.location.origin}${basePath}/action`,
        handleCodeInApp: true,
        // These settings help the link open in the PWA instead of browser
        iOS: {
            bundleId: 'com.style.wtf',
        },
        android: {
            packageName: 'com.style.wtf',
            installApp: false,
            minimumVersion: '1',
        },
    }
    return firebaseSendPasswordResetEmail(auth, email, actionCodeSettings)
}

/**
 * Confirm password reset code
 */
export async function confirmPasswordResetCode(
    oobCode: string,
    newPassword: string
): Promise<void> {
    return firebaseConfirmPasswordReset(auth, oobCode, newPassword)
}

// Export the auth instance
export { auth }
// Export analytics as undefined (not used - PostHog is used instead)
export { analytics }
